/**
 * AuditLogFilters - Filter controls for audit logs
 *
 * Features:
 * - Search by entity name or ID
 * - Filter by action type
 * - Filter by user
 * - Filter by entity type
 * - Date range selection
 * - Clear all filters
 */
import { useState, useCallback } from 'react';
import { cn } from '@respectly/ui';
import { Button } from '@respectly/ui';
import { Input } from '@respectly/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  X,
  Calendar,
  User,
  FileText,
  Zap,
} from 'lucide-react';
import type { AuditLogFilters as AuditLogFiltersType, SuperAdminAuditAction, SuperAdminEntityType } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface AuditLogFiltersProps {
  filters: AuditLogFiltersType;
  onFilterChange: <K extends keyof AuditLogFiltersType>(
    key: K,
    value: AuditLogFiltersType[K]
  ) => void;
  onClearFilters: () => void;
  activeCount?: number;
  className?: string;
}

// ============================================
// Constants
// ============================================

const ACTION_OPTIONS: { value: SuperAdminAuditAction; label: string }[] = [
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'suspend', label: 'Suspend' },
  { value: 'reactivate', label: 'Reactivate' },
  { value: 'cancel', label: 'Cancel' },
  { value: 'impersonate', label: 'Impersonate' },
  { value: 'password_reset', label: 'Password Reset' },
  { value: 'toggle_maintenance', label: 'Toggle Maintenance' },
  { value: 'clear_cache', label: 'Clear Cache' },
];

const ENTITY_TYPE_OPTIONS: { value: SuperAdminEntityType; label: string }[] = [
  { value: 'tenant', label: 'Account' },
  { value: 'user', label: 'User' },
  { value: 'plan', label: 'Plan' },
  { value: 'template', label: 'Template' },
  { value: 'super_admin_user', label: 'Admin User' },
  { value: 'feature_flag', label: 'Feature Flag' },
  { value: 'platform', label: 'Platform' },
];

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
];

// ============================================
// Component
// ============================================

export function AuditLogFilters({
  filters,
  onFilterChange,
  onClearFilters,
  activeCount = 0,
  className,
}: AuditLogFiltersProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [isExpanded, setIsExpanded] = useState(false);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      // Simple debounce using setTimeout
      const timeoutId = setTimeout(() => {
        onFilterChange('search', value || undefined);
      }, 300);
      return () => clearTimeout(timeoutId);
    },
    [onFilterChange]
  );

  const hasActiveFilters = activeCount > 0;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Main Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by entity name or ID..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
          {searchValue && (
            <button
              onClick={() => {
                setSearchValue('');
                onFilterChange('search', undefined);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-2">
          {/* Action Filter */}
          <Select
            value={filters.action || 'all'}
            onValueChange={(value) =>
              onFilterChange('action', value === 'all' ? undefined : (value as SuperAdminAuditAction))
            }
          >
            <SelectTrigger className="w-[140px]">
              <Zap className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {ACTION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date Range Filter */}
          <Select
            value={filters.dateRange || 'all'}
            onValueChange={(value) =>
              onFilterChange('dateRange', value === 'all' ? undefined : (value as 'all' | '24h' | '7days' | '30days' | '90days'))
            }
          >
            <SelectTrigger className="w-[150px]">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* More Filters Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              'gap-2',
              isExpanded && 'bg-slate-100 dark:bg-slate-800'
            )}
          >
            <Filter className="h-4 w-4" />
            More
            {activeCount > 0 && (
              <span className="ml-1 h-5 min-w-5 px-1 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                {activeCount}
              </span>
            )}
          </Button>

          {/* Clear All */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Expanded Filters */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* Entity Type */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    Entity Type
                  </label>
                  <Select
                    value={filters.entityType || 'all'}
                    onValueChange={(value) =>
                      onFilterChange('entityType', value === 'all' ? undefined : (value as SuperAdminEntityType))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {ENTITY_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Entity ID */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    Entity ID
                  </label>
                  <Input
                    placeholder="Enter entity ID..."
                    value={filters.entityId || ''}
                    onChange={(e) =>
                      onFilterChange('entityId', e.target.value || undefined)
                    }
                  />
                </div>

                {/* User ID */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    Admin User ID
                  </label>
                  <Input
                    placeholder="Enter user ID..."
                    value={filters.userId || ''}
                    onChange={(e) =>
                      onFilterChange('userId', e.target.value || undefined)
                    }
                  />
                </div>

                {/* Date Pickers would go here in a full implementation */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Custom Date Range
                  </label>
                  <p className="text-xs text-slate-400 py-2">
                    Use the date range dropdown for quick selection, or implement custom date pickers here.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Filters Display */}
      <AnimatePresence>
        {hasActiveFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="flex flex-wrap items-center gap-2"
          >
            <span className="text-xs text-slate-500">Active filters:</span>
            {filters.search && (
              <FilterPill
                label={`Search: "${filters.search}"`}
                onRemove={() => {
                  setSearchValue('');
                  onFilterChange('search', undefined);
                }}
              />
            )}
            {filters.action && (
              <FilterPill
                label={`Action: ${ACTION_OPTIONS.find((o) => o.value === filters.action)?.label}`}
                onRemove={() => onFilterChange('action', undefined)}
              />
            )}
            {filters.entityType && (
              <FilterPill
                label={`Type: ${ENTITY_TYPE_OPTIONS.find((o) => o.value === filters.entityType)?.label}`}
                onRemove={() => onFilterChange('entityType', undefined)}
              />
            )}
            {filters.entityId && (
              <FilterPill
                label={`Entity ID: ${filters.entityId.slice(0, 8)}...`}
                onRemove={() => onFilterChange('entityId', undefined)}
              />
            )}
            {filters.userId && (
              <FilterPill
                label={`User ID: ${filters.userId.slice(0, 8)}...`}
                onRemove={() => onFilterChange('userId', undefined)}
              />
            )}
            {filters.dateRange && filters.dateRange !== 'all' && (
              <FilterPill
                label={`Date: ${DATE_RANGE_OPTIONS.find((o) => o.value === filters.dateRange)?.label}`}
                onRemove={() => onFilterChange('dateRange', undefined)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// Filter Pill Component
// ============================================

function FilterPill({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs"
    >
      {label}
      <button
        onClick={onRemove}
        className="hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-full p-0.5"
      >
        <X className="h-3 w-3" />
      </button>
    </motion.span>
  );
}
